import React, { useState } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Collapse from "@mui/material/Collapse";
import { Chip } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CustomTableRow from "../../../../PreAuth/components/response/CustomTableRow";
import ItemInfoTable from "../../../../PreAuth/components/response/ItemInfoTable";
import { getColor } from "../../../../PreAuth/components/response/ResponseDetailsComponentNew";
import { useTranslation } from "react-i18next";

export default function EligibilityItemsTable({ data, searchTerm = "" }) {
  const { t } = useTranslation();
  const [expandedRows, setExpandedRows] = useState({});

  const items = (data || []).filter((item) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const benefitsMatch = (item.benefits || []).some((b) => {
      let benefit = {};
      try {
        benefit = typeof b === "string" ? JSON.parse(b) : b;
      } catch (e) {
        benefit = b || {};
      }
      return (
        benefit.type?.coding?.[0]?.code?.toLowerCase().includes(search) ||
        benefit.allowedMoney?.currency?.toLowerCase().includes(search)
      );
    });

    return (
      item.category_name?.toLowerCase().includes(search) ||
      item.description?.toLowerCase().includes(search) ||
      item.unit?.toLowerCase().includes(search) ||
      item.term?.toLowerCase().includes(search) ||
      item.network?.toLowerCase().includes(search) ||
      benefitsMatch
    );
  });

  const toggleRow = (index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <div className="w-full overflow-hidden border border-gray-300 rounded-[6px]">
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead className="bg-[#F4F7FB] text-[#475569] uppercase sticky top-0 z-10">
            <tr className="border-b border-gray-300">
              <th
                colSpan={1}
                className="px-2 py-1 border-r border-gray-300 text-center font-bold text-[10px] tracking-wider w-[3%]"
              >
                #
              </th>
              <th
                colSpan={4}
                className="px-2 py-1 border-r border-gray-300 text-center font-bold text-[10px] tracking-wider"
              >
                {t("Category")}
              </th>
              <th
                colSpan={4}
                className="px-2 py-1 text-center font-bold text-[10px] tracking-wider"
              >
                {t("Benefit")}
              </th>
            </tr>
            <tr className="bg-[#d1d5db] uppercase tracking-tight text-[#1e293b] text-[11px] font-[700]">
              <th className="px-1.5 py-1.5 border-r border-white/40 text-center w-[3%] leading-tight whitespace-nowrap">
                {t("Seq.")}
              </th>
              <th className="px-1.5 py-1.5 border-r border-white/40 text-left w-[25%] leading-tight whitespace-nowrap">
                {t("Item")}
              </th>
              <th className="px-1.5 py-1.5 border-r border-white/40 text-left w-[10%] leading-tight whitespace-nowrap">
                {t("Unit")}
              </th>
              <th className="px-1.5 py-1.5 border-r border-white/40 text-left w-[10%] leading-tight whitespace-nowrap">
                {t("Term")}
              </th>
              <th className="px-1.5 py-1.5 border-r border-white/40 text-left w-[10%] leading-tight whitespace-nowrap">
                {t("Network")}
              </th>
              <th className="px-1.5 py-1.5 border-r border-white/40 text-left w-[11.25%] leading-tight whitespace-nowrap">
                {t("Type")}
              </th>
              <th className="px-1.5 py-1.5 border-r border-white/40 text-left w-[11.25%] leading-tight whitespace-nowrap">
                {t("Money")}
              </th>
              <th className="px-1.5 py-1.5 border-r border-white/40 text-left w-[11.25%] leading-tight whitespace-nowrap">
                {t("Currency")}
              </th>
              <th className="px-1.5 py-1.5 text-left w-[11.25%] leading-tight whitespace-nowrap">
                {t("Other")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {items.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  className="px-4 py-8 text-center text-gray-400 italic"
                >
                  {t("No items found")}
                </td>
              </tr>
            ) : (
              items.map((item, iIdx) => {
                const benefits = item.benefits || [];
                const rowCount = Math.max(benefits.length, 1);

                return (
                  <React.Fragment key={iIdx}>
                    {/* Item Row(s) */}
                    {benefits.length === 0 ? (
                      <tr className="border-b border-gray-200 hover:bg-gray-50/50">
                        <td className="px-2 py-1.5 border-r border-gray-200 align-top text-center font-bold text-[10px] text-gray-500">
                          {iIdx + 1}
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-200 align-top">
                          <div className="font-bold text-[#1e293b] leading-tight text-[11px]">
                            {item.category_name}
                          </div>
                          <div className="text-[10px] text-gray-500 leading-tight mt-0.5 font-medium">
                            {item.description}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-200 align-top text-gray-600 font-medium">
                          {item.unit || "---"}
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-200 align-top text-gray-600 font-medium whitespace-nowrap">
                          {item.term || "---"}
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-200 align-top">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${item.network === "in" ? "bg-blue-50 text-blue-700" : "bg-orange-50 text-orange-700"}`}
                          >
                            {item.network || "---"}
                          </span>
                        </td>
                        <td
                          colSpan={4}
                          className="px-2 py-1.5 text-center text-gray-300 italic text-[10px]"
                        >
                          {t("No benefits data")}
                        </td>
                      </tr>
                    ) : (
                      benefits.map((b, bIdx) => {
                        let benefit = {};
                        try {
                          benefit = typeof b === "string" ? JSON.parse(b) : b;
                        } catch (e) {
                          benefit = b || {};
                        }

                        return (
                          <tr
                            key={bIdx}
                            className={`border-b border-gray-200 hover:bg-gray-50/50 transition-colors ${bIdx === benefits.length - 1 ? "border-b border-gray-300" : ""}`}
                          >
                            {bIdx === 0 && (
                              <>
                                <td
                                  rowSpan={rowCount}
                                  className="px-2 py-1.5 border-r border-gray-200 align-top text-center font-bold text-[10px] text-gray-500 bg-white"
                                >
                                  {iIdx + 1}
                                </td>
                                <td
                                  rowSpan={rowCount}
                                  className="px-2 py-1.5 border-r border-gray-200 align-top bg-white"
                                >
                                  <div className="font-bold text-[#1e293b] leading-tight text-[11px]">
                                    {item.category_name}
                                  </div>
                                  <div className="text-[10px] text-gray-500 leading-tight mt-0.5 font-medium">
                                    {item.description}
                                  </div>
                                </td>
                                <td
                                  rowSpan={rowCount}
                                  className="px-2 py-1.5 border-r border-gray-200 align-top text-gray-600 font-medium bg-white"
                                >
                                  {item.unit || "---"}
                                </td>
                                <td
                                  rowSpan={rowCount}
                                  className="px-2 py-1.5 border-r border-gray-200 align-top text-gray-600 font-medium bg-white"
                                >
                                  {item.term || "---"}
                                </td>
                                <td
                                  rowSpan={rowCount}
                                  className="px-2 py-1.5 border-r border-gray-200 align-top bg-white"
                                >
                                  <span
                                    className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${item.network === "in" ? "bg-blue-50 text-blue-700" : "bg-orange-50 text-orange-700"}`}
                                  >
                                    {item.network || "---"}
                                  </span>
                                </td>
                              </>
                            )}
                            <td className="px-2 py-1.5 border-r border-gray-200 text-gray-700 font-medium">
                              {benefit.type?.coding?.[0]?.code?.replace(
                                /-/g,
                                " ",
                              ) || "---"}
                            </td>
                            <td className="px-2 py-1.5 border-r border-gray-200 font-bold text-[#0070C2]">
                              {benefit.allowedMoney?.value ?? "---"}
                            </td>
                            <td className="px-2 py-1.5 border-r border-gray-200 text-gray-600">
                              {benefit.allowedMoney?.currency || "---"}
                            </td>
                            <td className="px-2 py-1.5 text-gray-600 font-medium">
                              {benefit.allowedUnsignedInt !== undefined
                                ? `${benefit.allowedUnsignedInt}%`
                                : benefit.allowedString || "---"}
                            </td>
                          </tr>
                        );
                      })
                    )}
                    {/* Spacer Row between items */}
                    {iIdx < items.length - 1 && (
                      <tr className="h-1 bg-gray-100">
                        <td
                          colSpan={9}
                          className="p-0 border-y border-gray-300"
                        ></td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  /*
  // --- OLD CODE COMMENTED OUT ---
  // return (
  //   <div className="w-full overflow-hidden border border-gray-200 rounded-md">
  //     <div className="overflow-x-auto">
  //       <table className="w-full text-sm border-collapse">
  //         ... (old table content)
  //       </table>
  //     </div>
  //   </div>
  // );
  */
}
